/**
 * Exam2: fixed body-system rows + AI extras, with normal vs abnormal dropdown coloring.
 */

const sortAlpha = (items) =>
  [...items].sort((a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: "base" }));

const uniqTrimmed = (items) => [
  ...new Set(
    (Array.isArray(items) ? items : [])
      .map((item) => String(item || "").trim())
      .filter(Boolean),
  ),
];

const normalizeSystemKey = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** Canonical base systems always shown on Exam2 (AI may add extras). */
export const BASE_EXAM2_SYSTEM_DEFS = [
  {
    id: "general",
    name: "GENERAL",
    options: ["WDWN male in NAD", "WDWN female in NAD", "WDWN adult in NAD"],
    normalOptions: ["WDWN male in NAD", "WDWN female in NAD", "WDWN adult in NAD"],
  },
  {
    id: "vitals",
    name: "VITAL SIGNS",
    options: ["WNL", "Tachycardic", "Bradycardic", "Hypertensive", "Hypotensive", "Febrile", "Hypoxic"],
    normalOptions: ["WNL"],
  },
  {
    id: "heent",
    name: "HEENT",
    options: [
      "Normocephalic atraumatic",
      "PERRL",
      "EOMI",
      "TMs clear",
      "Oropharynx clear",
      "JVD present",
      "Mucous membranes dry",
      "Scleral icterus",
    ],
    normalOptions: ["Normocephalic atraumatic", "PERRL", "EOMI", "TMs clear", "Oropharynx clear"],
  },
  {
    id: "breast",
    name: "BREAST",
    options: [
      "No masses or nipple discharge",
      "Gynecomastia absent",
      "Deferred",
      "Mass palpated",
      "Nipple discharge",
      "Gynecomastia present",
    ],
    normalOptions: ["No masses or nipple discharge", "Gynecomastia absent", "Deferred"],
  },
  {
    id: "heart",
    name: "HEART",
    options: [
      "RRR no murmur",
      "RRR s murmur",
      "Irregularly irregular",
      "Tachycardic",
      "Bradycardic",
      "S3 present",
      "S4 present",
      "Elevated JVP",
    ],
    normalOptions: ["RRR no murmur", "RRR s murmur"],
  },
  {
    id: "lungs",
    name: "LUNGS",
    options: [
      "CTAB",
      "Clear bilaterally",
      "Bilateral crackles",
      "Unilateral crackles",
      "Wheezes",
      "Diminished breath sounds",
      "Rhonchi",
    ],
    normalOptions: ["CTAB", "Clear bilaterally"],
  },
  {
    id: "abdomen",
    name: "ABDOMEN",
    options: [
      "Soft NT ND",
      "Soft nontender",
      "Tender",
      "Guarding",
      "Rebound",
      "Distended",
      "Hepatomegaly",
    ],
    normalOptions: ["Soft NT ND", "Soft nontender"],
  },
  {
    id: "extremities",
    name: "EXTREMITIES",
    options: [
      "No edema",
      "Trace bilateral edema",
      "1+ bilateral edema",
      "2+ bilateral edema",
      "Unilateral edema",
      "Warm well perfused",
      "Cool extremities",
    ],
    normalOptions: ["No edema", "Warm well perfused"],
  },
  {
    id: "gu",
    name: "GU",
    options: [
      "Normal male external genitalia",
      "Normal female external genitalia",
      "Deferred",
      "Testicular mass",
      "Urethral discharge",
      "Cervical motion tenderness",
      "Adnexal tenderness",
    ],
    normalOptions: [
      "Normal male external genitalia",
      "Normal female external genitalia",
      "Deferred",
    ],
  },
  {
    id: "neuro",
    name: "NEURO",
    options: [
      "Grossly intact",
      "CN II–XII intact",
      "Alert and oriented x3",
      "Focal weakness",
      "Facial droop",
      "Asymmetric pupils",
      "Sensory deficit",
      "Ataxia",
    ],
    normalOptions: ["Grossly intact", "CN II–XII intact", "Alert and oriented x3"],
  },
  {
    id: "skin",
    name: "SKIN",
    options: ["Warm dry intact", "Diaphoretic", "Pallor", "Cyanosis", "Jaundice", "Rash", "Ecchymosis"],
    normalOptions: ["Warm dry intact"],
  },
];

const BASE_ID_SET = new Set(BASE_EXAM2_SYSTEM_DEFS.map((def) => def.id));
const BASE_NAME_KEYS = new Set(BASE_EXAM2_SYSTEM_DEFS.map((def) => normalizeSystemKey(def.name)));

/**
 * Prefer shared record demographics; fall back to VisitStart self-report.
 * @returns {"male"|"female"|null}
 */
export function extractPatientSexKey(patientContext) {
  const fromRecord = patientContext?.record?.demographics?.sex;
  const fromIntake = patientContext?.visitStartIntake?.elementAnswers?.identity?.sex;
  const raw = String(fromRecord || fromIntake || "")
    .trim()
    .toLowerCase();
  if (raw === "male" || raw === "m") return "male";
  if (raw === "female" || raw === "f") return "female";
  return null;
}

export function defaultGeneralFinding(sexKey) {
  if (sexKey === "male") return "WDWN male in NAD";
  if (sexKey === "female") return "WDWN female in NAD";
  return "WDWN adult in NAD";
}

/**
 * Built-in sex-aware preferred normals (used until the clinician sets their own).
 * @param {string} systemId
 * @param {"male"|"female"|null|undefined} sexKey
 * @returns {string|null}
 */
export function sexAwarePreferredNormal(systemId, sexKey) {
  const id = String(systemId || "").trim();
  if (id === "general") return defaultGeneralFinding(sexKey);
  if (id === "gu") {
    if (sexKey === "male") return "Normal male external genitalia";
    if (sexKey === "female") return "Normal female external genitalia";
    return "Deferred";
  }
  if (id === "breast") {
    if (sexKey === "male") return "Gynecomastia absent";
    if (sexKey === "female") return "No masses or nipple discharge";
    return "Deferred";
  }
  return null;
}

/**
 * Preferred normal for Ctrl+Tab fill: clinician override → sex-aware → first catalog normal.
 * @param {{ id?: string, normalOptions?: string[] }} system
 * @param {{ sexKey?: string|null, preferredBySystemId?: Record<string, string> }} [ctx]
 */
export function getPreferredExam2Normal(system, { sexKey = null, preferredBySystemId = {} } = {}) {
  const id = String(system?.id || "").trim();
  const fromUser = String(preferredBySystemId?.[id] || "").trim();
  if (fromUser) return fromUser;
  const fromSex = sexAwarePreferredNormal(id, sexKey);
  if (fromSex) return fromSex;
  const normals = Array.isArray(system?.normalOptions) ? system.normalOptions : [];
  return String(normals[0] || "").trim();
}

function cloneSystemDef(def, value = []) {
  const options = sortAlpha(uniqTrimmed(def.options));
  const normalOptions = sortAlpha(uniqTrimmed(def.normalOptions));
  return {
    id: def.id,
    name: def.name,
    source: def.source || "base",
    options,
    normalOptions,
    value: normalizeExam2Values(value),
  };
}

/**
 * Fresh Exam2 rows from the base catalog (GENERAL prefilled from demographics when known).
 */
export function createBaseExam2Systems(patientContext = null) {
  const sexKey = extractPatientSexKey(patientContext);
  const generalDefault = defaultGeneralFinding(sexKey);
  return BASE_EXAM2_SYSTEM_DEFS.map((def) =>
    cloneSystemDef(def, def.id === "general" ? [generalDefault] : []),
  );
}

/**
 * Normalize a system value to a list of findings (supports legacy single strings).
 * @param {string|string[]|null|undefined} value
 * @returns {string[]}
 */
export function normalizeExam2Values(value) {
  if (Array.isArray(value)) return uniqTrimmed(value);
  const text = String(value || "").trim();
  if (!text) return [];
  // Persisted multi-select joins with "; "
  if (text.includes(";")) return uniqTrimmed(text.split(/\s*;\s*/));
  return [text];
}

export function formatExam2ValueText(value) {
  return normalizeExam2Values(value).join("; ");
}

export function isNormalExam2Finding(system, value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) return false;
  const normals = Array.isArray(system?.normalOptions) ? system.normalOptions : [];
  return normals.some((option) => option.toLowerCase() === trimmed.toLowerCase());
}

/**
 * Normalize AI / persisted Exam2 extras (non-base systems or option addenda).
 */
export function processExam2Extras(extras) {
  if (!Array.isArray(extras)) return [];

  return extras
    .map((raw, index) => {
      const name = String(raw?.name || raw?.system || raw?.area || "")
        .trim()
        .toUpperCase();
      if (!name) return null;
      const options = sortAlpha(
        uniqTrimmed(raw.options || raw.findings || raw.anticipatedFindings || []),
      );
      const normalOptions = sortAlpha(
        uniqTrimmed(raw.normalOptions || raw.normal_options || raw.normals || []),
      );
      const id =
        String(raw.id || "").trim() ||
        `ai-${normalizeSystemKey(name) || index}-${Date.now()}`;
      return {
        id,
        name,
        source: "ai",
        options,
        normalOptions,
        value: normalizeExam2Values(raw.value),
      };
    })
    .filter(Boolean);
}

/**
 * Merge base systems with AI extras. Preserves user-entered values by id/name when possible.
 * AI extras that match a base system name contribute options only (do not duplicate the row).
 */
export function mergeExam2Systems({
  patientContext = null,
  extras = [],
  previousSystems = [],
} = {}) {
  const prevById = new Map();
  const prevByName = new Map();
  (Array.isArray(previousSystems) ? previousSystems : []).forEach((row) => {
    if (row?.id) prevById.set(row.id, row);
    const key = normalizeSystemKey(row?.name);
    if (key) prevByName.set(key, row);
  });

  const processedExtras = processExam2Extras(extras);
  const optionAddenda = new Map(); // base name key → extra options/normals
  const trueExtras = [];

  processedExtras.forEach((extra) => {
    const key = normalizeSystemKey(extra.name);
    if (BASE_NAME_KEYS.has(key) || BASE_ID_SET.has(extra.id)) {
      const existing = optionAddenda.get(key) || { options: [], normalOptions: [] };
      optionAddenda.set(key, {
        options: [...existing.options, ...extra.options],
        normalOptions: [...existing.normalOptions, ...extra.normalOptions],
      });
      return;
    }
    trueExtras.push(extra);
  });

  const baseRows = BASE_EXAM2_SYSTEM_DEFS.map((def) => {
    const prev = prevById.get(def.id) || prevByName.get(normalizeSystemKey(def.name));
    const addendum = optionAddenda.get(normalizeSystemKey(def.name)) || {
      options: [],
      normalOptions: [],
    };
    const options = sortAlpha(uniqTrimmed([...def.options, ...addendum.options, ...(prev?.options || [])]));
    const normalOptions = sortAlpha(
      uniqTrimmed([...def.normalOptions, ...addendum.normalOptions, ...(prev?.normalOptions || [])]),
    );
    let value = normalizeExam2Values(prev?.value);
    if (!value.length && def.id === "general") {
      value = [defaultGeneralFinding(extractPatientSexKey(patientContext))];
    }
    return {
      id: def.id,
      name: def.name,
      source: "base",
      options,
      normalOptions,
      value,
    };
  });

  const extraRows = trueExtras.map((extra) => {
    const prev = prevById.get(extra.id) || prevByName.get(normalizeSystemKey(extra.name));
    return {
      ...extra,
      options: sortAlpha(uniqTrimmed([...(extra.options || []), ...(prev?.options || [])])),
      normalOptions: sortAlpha(
        uniqTrimmed([...(extra.normalOptions || []), ...(prev?.normalOptions || [])]),
      ),
      value: normalizeExam2Values(prev?.value?.length ? prev.value : extra.value),
    };
  });

  // Keep any previous AI rows that are not in the latest extras (user-added / prior session).
  const knownIds = new Set([...baseRows, ...extraRows].map((row) => row.id));
  const orphanExtras = (Array.isArray(previousSystems) ? previousSystems : [])
    .filter((row) => row?.source === "ai" && row.id && !knownIds.has(row.id))
    .map((row) => ({
      ...row,
      name: String(row.name || "").trim().toUpperCase(),
      options: sortAlpha(uniqTrimmed(row.options)),
      normalOptions: sortAlpha(uniqTrimmed(row.normalOptions)),
      value: normalizeExam2Values(row.value),
      source: "ai",
    }));

  return [...baseRows, ...extraRows, ...orphanExtras];
}

export function formatExam2ObjectiveLine(system) {
  const name = String(system?.name || "").trim();
  const value = formatExam2ValueText(system?.value);
  if (!name || !value) return "";
  return `- ${name}: ${value}`;
}

export function buildExam2ObjectiveLines(systems = []) {
  return (Array.isArray(systems) ? systems : [])
    .map((system) => formatExam2ObjectiveLine(system))
    .filter(Boolean);
}

/** Keyword → Exam2 system id for placing differential tips under the matching row. */
const SYSTEM_TIP_MATCHERS = [
  {
    id: "vitals",
    patterns: [/vital/, /orthostatic/, /blood pressure/, /\bbp\b/, /heart rate/, /\bhr\b/, /temperature/, /pulse ox/, /spo2/, /tachycard/, /bradycard/, /hypotens/, /hypertens/],
  },
  {
    id: "heent",
    patterns: [/heent/, /\bhead\b/, /\beyes?\b/, /\bears?\b/, /\bnose\b/, /throat/, /pharyn/, /tympanic/, /sclera/, /oropharyn/, /mucous membrane/, /normocephalic/, /perrl/, /eomi/],
  },
  {
    id: "breast",
    patterns: [/breast/, /mammar/, /gynecomast/, /nipple/],
  },
  {
    id: "heart",
    patterns: [/heart/, /cardiac/, /cardiovasc/, /\bjvp\b/, /\bjvd\b/, /murmur/, /precordi/, /s3\b/, /s4\b/, /jugular/],
  },
  {
    id: "lungs",
    patterns: [/lung/, /pulmon/, /respirat/, /chest exam/, /breath sound/, /crackles/, /wheez/, /rhonchi/, /auscult/],
  },
  {
    id: "abdomen",
    patterns: [/abdomen/, /abdominal/, /\bgui\b/, /hepatomeg/, /splenomeg/, /bowel sound/, /rebound/, /guarding/],
  },
  {
    id: "extremities",
    patterns: [/extremit/, /edema/, /peripheral/, /capillary refill/, /pulses?\b/, /calf/, /leg exam/],
  },
  {
    id: "gu",
    patterns: [/\bgu\b/, /genit/, /pelvic/, /testicul/, /scrot/, /cervical motion/, /adnexal/, /prostat/],
  },
  {
    id: "neuro",
    patterns: [/neuro/, /neurolog/, /cranial nerve/, /\bcn\b/, /mental status/, /focal weak/, /gait/, /reflex/, /sensory/, /orient/],
  },
  {
    id: "skin",
    patterns: [/\bskin\b/, /dermat/, /rash/, /diaphoret/, /cyanosis/, /jaundice/, /ecchymos/, /pallor/],
  },
  {
    id: "general",
    patterns: [/general/, /\bnad\b/, /appearance/, /distress/, /wdwn/, /toxic/],
  },
];

function tipSearchText(finding) {
  return [
    finding?.name,
    finding?.rationale,
    ...(Array.isArray(finding?.anticipatedFindings) ? finding.anticipatedFindings : []),
  ]
    .map((part) => String(part || "").toLowerCase())
    .join(" ");
}

/**
 * Map a focused exam finding to the best Exam2 system id (or null).
 */
export function matchExamFindingToSystemId(finding, systems = []) {
  const text = tipSearchText(finding);
  if (!text.trim()) return null;

  const systemIds = new Set(
    (Array.isArray(systems) ? systems : []).map((row) => row.id).filter(Boolean),
  );

  for (const row of Array.isArray(systems) ? systems : []) {
    const name = String(row?.name || "").trim().toLowerCase();
    if (name && text.includes(name.toLowerCase())) return row.id;
  }

  for (const matcher of SYSTEM_TIP_MATCHERS) {
    if (systemIds.size > 0 && !systemIds.has(matcher.id)) continue;
    if (matcher.patterns.some((pattern) => pattern.test(text))) return matcher.id;
  }
  return null;
}

/**
 * Group Exam1 AI findings into tips keyed by Exam2 system id.
 * Unmatched tips land under `_other`.
 *
 * @returns {{ bySystemId: Record<string, Array>, other: Array, hasTips: boolean }}
 */
export function groupExamTipsBySystem(examFindings = [], diagnoses = [], systems = []) {
  const preferred = new Set(
    (Array.isArray(diagnoses) ? diagnoses : [])
      .map((d) => String(d?.condition || d?.name || "").trim().toLowerCase())
      .filter(Boolean),
  );

  /** @type {Record<string, Array>} */
  const bySystemId = {};
  const other = [];

  (Array.isArray(examFindings) ? examFindings : []).forEach((finding) => {
    const examName = String(finding?.name || "").trim();
    if (!examName) return;
    const rationale = String(finding?.rationale || "").trim();
    const lookFor = uniqTrimmed(finding?.anticipatedFindings).slice(0, 8);
    const linked = Array.isArray(finding?.linkedDiagnoses)
      ? finding.linkedDiagnoses.map((d) => String(d || "").trim()).filter(Boolean)
      : [];
    const diagnosesForTip =
      preferred.size > 0
        ? linked.filter((name) => preferred.has(name.toLowerCase()))
        : linked;

    // Keep tips that are linked to the current differential, or unlinked general tips.
    if (preferred.size > 0 && linked.length > 0 && diagnosesForTip.length === 0) {
      return;
    }

    const tip = {
      examName,
      rationale,
      lookFor,
      diagnoses: diagnosesForTip.length ? diagnosesForTip : linked,
    };
    const systemId = matchExamFindingToSystemId(finding, systems);
    if (systemId) {
      if (!bySystemId[systemId]) bySystemId[systemId] = [];
      bySystemId[systemId].push(tip);
    } else {
      other.push(tip);
    }
  });

  const hasTips =
    other.length > 0 || Object.values(bySystemId).some((tips) => tips.length > 0);
  return { bySystemId, other, hasTips };
}

/**
 * Group Exam1 AI findings into differential-linked tips (legacy / tests).
 * @param {Array} examFindings
 * @param {Array} [diagnoses] optional filter / preferred order (selected differential)
 */
export function groupExamTipsByDiagnosis(examFindings = [], diagnoses = []) {
  const diagnosisOrder = (Array.isArray(diagnoses) ? diagnoses : [])
    .map((d) => String(d?.condition || d?.name || "").trim())
    .filter(Boolean);
  const preferred = new Set(diagnosisOrder.map((name) => name.toLowerCase()));

  /** @type {Map<string, { diagnosis: string, tips: Array<{ examName: string, rationale: string, lookFor: string[] }> }>} */
  const groups = new Map();

  const ensureGroup = (diagnosis) => {
    const key = diagnosis.toLowerCase();
    if (!groups.has(key)) {
      groups.set(key, { diagnosis, tips: [] });
    }
    return groups.get(key);
  };

  (Array.isArray(examFindings) ? examFindings : []).forEach((finding) => {
    const examName = String(finding?.name || "").trim();
    if (!examName) return;
    const rationale = String(finding?.rationale || "").trim();
    const lookFor = uniqTrimmed(finding?.anticipatedFindings).slice(0, 8);
    const linked = Array.isArray(finding?.linkedDiagnoses)
      ? finding.linkedDiagnoses.map((d) => String(d || "").trim()).filter(Boolean)
      : [];

    const targets = linked.length > 0 ? linked : ["Clinical exam tips"];

    targets.forEach((diagnosis) => {
      const isCatchAll = diagnosis === "Clinical exam tips";
      if (preferred.size > 0 && !isCatchAll && !preferred.has(diagnosis.toLowerCase())) {
        return;
      }
      ensureGroup(diagnosis).tips.push({ examName, rationale, lookFor });
    });
  });

  const ordered = [];
  const used = new Set();
  diagnosisOrder.forEach((name) => {
    const key = name.toLowerCase();
    if (groups.has(key)) {
      ordered.push(groups.get(key));
      used.add(key);
    }
  });
  groups.forEach((group, key) => {
    if (!used.has(key)) ordered.push(group);
  });
  return ordered.filter((group) => group.tips.length > 0);
}
